import React from "react";
import { observer } from "mobx-react";
import { Button, Input, InputNumber, Select, DatePicker } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import moment from "moment";

import { COUNTRIES } from "./countries";

import "./style.scoped.scss";

const { RangePicker } = DatePicker;

const isRequired = (field, values) => {
    if (field.required) {
        return true;
    }
    if (field.requiredIf) {
        return values[field.requiredIf.field] === field.requiredIf.value;
    }
    return false;
};

// `options` entries are either a bare string (value === label) or an explicit
// `{ value, label }` pair — needed where the engine matches a value that reads
// badly in the UI, e.g. `update_for_changes` wants "5-year".
const toOptions = (options = []) =>
    options.map((o) => (typeof o === "object" ? o : { value: o, label: o }));

const FieldControl = observer(({ field, values, setValue }) => {
    const value = values[field.name];

    const setMoment = (m) => {
        setValue(field.name, m ? m.toISOString() : null);
    };
    const setRange = (m) => {
        if (!m) {
            return setValue(field.name, null);
        }
        setValue(field.name, [m[0]?.toISOString() || null, m[1]?.toISOString() || null]);
    };

    switch (field.type) {
        case "text":
            return (
                <Input
                    value={value || ""}
                    placeholder={field.placeholder}
                    onChange={(e) => setValue(field.name, e.target.value)}
                />
            );
        case "textarea":
            return (
                <Input.TextArea
                    rows={2}
                    value={value || ""}
                    onChange={(e) => setValue(field.name, e.target.value)}
                />
            );
        case "long_text":
            return (
                <Input.TextArea
                    rows={4}
                    value={value || ""}
                    onChange={(e) => setValue(field.name, e.target.value)}
                />
            );
        case "number":
            return (
                <InputNumber
                    value={value ?? undefined}
                    precision={field.precision}
                    min={field.min}
                    onChange={(v) => setValue(field.name, v)}
                />
            );
        case "boolean":
            return (
                <Select
                    className="narrow"
                    value={value === true ? "yes" : value === false ? "no" : undefined}
                    placeholder="Select"
                    allowClear
                    onChange={(v) =>
                        setValue(field.name, v === "yes" ? true : v === "no" ? false : null)
                    }
                    options={[
                        { value: "yes", label: "Yes" },
                        { value: "no", label: "No" },
                    ]}
                />
            );
        case "select":
            return (
                <Select
                    value={value || undefined}
                    placeholder="Select"
                    allowClear
                    onChange={(v) => setValue(field.name, v || null)}
                    options={toOptions(field.options)}
                />
            );
        case "country":
            return (
                <Select
                    showSearch
                    value={value || undefined}
                    placeholder="Select a country"
                    allowClear
                    optionFilterProp="label"
                    onChange={(v) => setValue(field.name, v || null)}
                    options={COUNTRIES.map((c) => ({ value: c, label: c }))}
                />
            );
        case "date":
            return <DatePicker value={value ? moment(value) : null} onChange={setMoment} />;
        case "date_range":
            return (
                <RangePicker
                    value={
                        Array.isArray(value) && value[0] && value[1]
                            ? [moment(value[0]), moment(value[1])]
                            : null
                    }
                    onChange={setRange}
                />
            );
        case "string_list":
            return <StringList value={value} onChange={(v) => setValue(field.name, v)} />;
        case "clinical_entry_list":
            return <ClinicalEntryList value={value} onChange={(v) => setValue(field.name, v)} />;
        default:
            return null;
    }
});

const StringList = ({ value, onChange }) => {
    const list = Array.isArray(value) ? value : [];

    const update = (i, v) => {
        const next = [...list];
        next[i] = v;
        onChange(next);
    };
    const remove = (i) => {
        const next = [...list];
        next.splice(i, 1);
        onChange(next);
    };
    const add = () => {
        onChange([...list, ""]);
    };

    return (
        <div className="cer-string-list">
            {list.map((v, i) => (
                <div className="row" key={i}>
                    <Input value={v} onChange={(e) => update(i, e.target.value)} />
                    <Button icon={<CloseOutlined />} onClick={() => remove(i)} />
                </div>
            ))}
            <Button className="add-btn" icon={<PlusOutlined />} onClick={add}>
                Add
            </Button>
        </div>
    );
};

// The contract's `{ name, device?, source_document? }[]` shape — used by
// `clinical_study_titles`, `non_clinical_tests` and `biocompatibility_tests`. A
// plain `string[]` fails the engine's schema outright, so these fields cannot
// reuse `StringList`. `name` is the only required part of an entry; blank rows
// are dropped on save.
const ClinicalEntryList = ({ value, onChange }) => {
    const list = Array.isArray(value) ? value : [];

    const update = (i, key, v) => {
        const next = [...list];
        next[i] = { ...next[i], [key]: v };
        onChange(next);
    };
    const remove = (i) => {
        const next = [...list];
        next.splice(i, 1);
        onChange(next);
    };
    const add = () => {
        onChange([...list, { name: "" }]);
    };

    return (
        <div className="cer-entry-list">
            {list.map((entry, i) => (
                <div className="entry" key={i}>
                    <Input
                        placeholder="Name (required)"
                        value={entry?.name || ""}
                        onChange={(e) => update(i, "name", e.target.value)}
                    />
                    <Input
                        placeholder="Device (optional)"
                        value={entry?.device || ""}
                        onChange={(e) => update(i, "device", e.target.value)}
                    />
                    <Input
                        placeholder="Source document (optional)"
                        value={entry?.source_document || ""}
                        onChange={(e) => update(i, "source_document", e.target.value)}
                    />
                    <Button icon={<CloseOutlined />} onClick={() => remove(i)} />
                </div>
            ))}
            <Button className="add-btn" icon={<PlusOutlined />} onClick={add}>
                Add
            </Button>
        </div>
    );
};

const Field = observer(({ field, values, setValue }) => {
    const required = isRequired(field, values);
    return (
        <div className="cer-field">
            <label>
                {field.label}
                {required ? <span className="required-marker"> *</span> : null}
            </label>
            {field.description && <div className="field-description">{field.description}</div>}
            <FieldControl field={field} values={values} setValue={setValue} />
        </div>
    );
});

/**
 * Every field belonging to one panel, grouped into its blocks.
 *
 * A block listed in the template's `blockSections` is only rendered when one of
 * those sections is selected — that is what keeps the CER's shared `core` and
 * equivalence fields out of the way of someone who only asked for, say, the
 * literature section.
 */
const FieldList = observer(({ template, section, values, setValue, selectedSections = [] }) => {
    const selected = new Set(selectedSections);

    // Preserve declaration order, and keep each block contiguous.
    const blocks = [];
    for (const field of template.fields) {
        if (field.section !== section) {
            continue;
        }
        const name = field.block || "";
        const last = blocks[blocks.length - 1];
        if (last && last.name === name) {
            last.fields.push(field);
        } else {
            blocks.push({ name, fields: [field] });
        }
    }

    return (
        <>
            {blocks.map((block) => {
                const id = `${section}:${block.name}`;
                const limitedTo = template.blockSections[id];
                if (limitedTo && !limitedTo.some((key) => selected.has(key))) {
                    return null;
                }
                return (
                    <div className="cer-field-block" key={id}>
                        {template.blockTitles[id] && <h4>{template.blockTitles[id]}</h4>}
                        {template.blockDescriptions[id] && (
                            <div className="block-description">
                                {template.blockDescriptions[id]}
                            </div>
                        )}
                        {block.fields.map((field) => (
                            <Field
                                key={field.name}
                                field={field}
                                values={values}
                                setValue={setValue}
                            />
                        ))}
                    </div>
                );
            })}
        </>
    );
});

export default FieldList;
